/**
 * One registration case on "Mes relances": stage bar, follow-up sequence,
 * and the actions that advance it (`docs/API.md` §7).
 */
import { useState } from 'react';

import { advanceRegistrationCase } from '../../../api/household';
import { ApiError } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import { Button, Select, Sheet } from '../../../components/ui';
import { formatShortDate } from '../format';
import { t } from '../../../i18n/fr';
import { CASE_STAGE_LABEL_KEYS, CLOSE_REASON_LABEL_KEYS } from '../labels';
import { MailSequence } from './MailSequence';

import type { ApiErrorBody, CaseStage, RegistrationCase, User } from '../../../api/types';
import type { CloseReason } from '../labels';
import type { CaseMessage } from './MailSequence';

/**
 * Whether the signed-in user should be offered "Réarmer" on a failed
 * follow-up message: a commune admin, of any commune. `RegistrationCase`
 * never carries its commune (`docs/API.md` §7 — the backend resolves it
 * from the door), so, like the mocks' own single-commune fixture, this is
 * the frontend's best available signal; the server still enforces the real
 * "admin of *this case's* commune" check on the `rearm` call itself.
 *
 * @param user - The signed-in user, or `undefined` while auth is loading.
 * @returns Whether the caller administers at least one commune.
 */
function isCommuneAdmin(user: User | undefined): boolean {
  return user?.memberships.some((membership) => membership.role === 'commune_admin') ?? false;
}

export interface CaseCardProps {
  registrationCase: RegistrationCase;
  onUpdated: (updated: RegistrationCase) => void;
}

const STAGE_ORDER: CaseStage[] = ['info_given', 'filed', 'confirmed', 'closed'];

const CLOSE_REASON_OPTIONS: CloseReason[] = [
  'confirmed',
  'unsubscribed',
  'no_answer',
  'withdrawn',
  'other',
];

/**
 * Render one registration case's card: who, stage bar, follow-up sequence
 * and the actions that move it forward.
 *
 * @param props - The case and a callback run with the server's fresh copy
 *   after a successful transition.
 * @returns The card element.
 */
export function CaseCard({ registrationCase, onUpdated }: CaseCardProps) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closeSheetOpen, setCloseSheetOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<CloseReason>('confirmed');

  const stage = registrationCase.stage;
  const currentIndex = STAGE_ORDER.indexOf(stage);
  const canRearm = isCommuneAdmin(user);

  /** Fold a rearmed message back into the case, and hand the fresh copy up. */
  function handleMessageRearmed(rearmedMessage: CaseMessage) {
    onUpdated({
      ...registrationCase,
      messages: registrationCase.messages.map((message) =>
        message.id === rearmedMessage.id ? rearmedMessage : message,
      ),
    });
  }

  async function advance(nextStage: 'filed' | 'confirmed' | 'closed', reason?: CloseReason) {
    setBusy(true);
    setError(null);
    try {
      const updated = await advanceRegistrationCase(registrationCase.id, {
        stage: nextStage,
        close_reason: reason ?? '',
      });
      onUpdated(updated);
      setCloseSheetOpen(false);
    } catch (cause) {
      if (cause instanceof ApiError) {
        const body = cause.body as ApiErrorBody | undefined;
        setError(body?.detail ?? t('household.cases.advanceError'));
      } else {
        setError(t('household.cases.advanceError'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relance">
      <div className="qui">{registrationCase.first_name || registrationCase.door_label}</div>
      <span className="muted">
        {t('household.cases.openedOn', { date: formatShortDate(registrationCase.opened_at) })}
      </span>
      <div className="etapes" role="img" aria-label={t(CASE_STAGE_LABEL_KEYS[stage])}>
        {STAGE_ORDER.map((s, index) => (
          <span
            key={s}
            className={index < currentIndex ? 'ok' : index === currentIndex ? 'now' : ''}
          />
        ))}
      </div>
      <MailSequence
        messages={registrationCase.messages}
        caseId={registrationCase.id}
        canRearm={canRearm}
        onMessageRearmed={handleMessageRearmed}
      />
      {error ? (
        <p className="rappel" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        {stage === 'info_given' ? (
          <Button variant="primary" disabled={busy} onClick={() => void advance('filed')}>
            {t('household.cases.actions.filed')}
          </Button>
        ) : null}
        {stage === 'filed' ? (
          <Button variant="primary" disabled={busy} onClick={() => void advance('confirmed')}>
            {t('household.cases.actions.confirmed')}
          </Button>
        ) : null}
        {stage !== 'closed' ? (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setError(null);
              setCloseSheetOpen(true);
            }}
          >
            {t('household.cases.actions.close')}
          </Button>
        ) : null}
      </div>
      <Sheet
        open={closeSheetOpen}
        onClose={() => setCloseSheetOpen(false)}
        title={t('household.cases.closeReasonTitle')}
      >
        <Select
          label={t('household.cases.closeReasonTitle')}
          value={closeReason}
          onChange={(event) => setCloseReason(event.target.value as CloseReason)}
          options={CLOSE_REASON_OPTIONS.map((reason) => ({
            value: reason,
            label: t(CLOSE_REASON_LABEL_KEYS[reason]),
          }))}
        />
        <Button
          variant="danger"
          fullWidth
          disabled={busy}
          onClick={() => void advance('closed', closeReason)}
        >
          {t('household.cases.closeReasonSubmit')}
        </Button>
      </Sheet>
    </div>
  );
}
